/* eslint-disable kf/const-naming-convention */
import React, { useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";
import { UserIcon, AVATAR_SIZE, OverflowText } from "@widgets/datadisplay";

import { isEmpty } from "base/utils";

import { BaseDropdown } from "../base.jsx";
import { SingleSelectWidget } from "./singleselect.jsx";
import { FieldHint, FieldErrors } from "../../fields/index.jsx";
import { MultiSelectWidget } from "./multiselect.jsx";
import { MultiUserSelectWidget } from "./multiuserselect.jsx";
import { SingleUserSelectWidget } from "./singleuserselect.jsx";

import styles from "./style.css";

import DropDownIcon from "./DropDownIcon.svg";
import ClearIcon from "../clear.svg";

// eslint-disable-next-line kf/const-naming-convention
export const SingleSelectDropDownMenu = SingleSelectWidget;
// eslint-disable-next-line kf/const-naming-convention
export const MultiSelectDropdownMenu = MultiSelectWidget;
// eslint-disable-next-line kf/const-naming-convention
export const MultiSelectUserDropdownMenu = MultiUserSelectWidget;
// eslint-disable-next-line kf/const-naming-convention
export const SingleUserSelectDropdownMenu = SingleUserSelectWidget;

export function SleekDropDown(props) {
  let {
    label = null,
    value = null,
    placeholder = null,
    listRenderer = null,
    onClear = null,
    disabled = false,
    readOnly = false,
    errors = [],
    hint = null,
    id = "sleekdropdown",
    onClose = () => {},
    onOpen = () => {},
    isOpen = false,
    className,
    ...rest
  } = props;
  let [dropDownStatus, setDropDownStatus] = useState(isOpen);

  function handleDropDown(action) {
    setDropDownStatus(action);
  }

  useEffect(
    function updateDropDownStatus() {
      setDropDownStatus(props.isOpen);
    },
    [props.isOpen]
  );

  const labelWithProps = React.isValidElement(label)
    ? React.cloneElement(label, { value })
    : label;

  return (
    <BaseDropdown
      destroyOnClose={true}
      closeOnOutsideClick={true}
      onOpen={() => {
        onOpen();
        handleDropDown(true);
      }}
      onClose={() => {
        handleDropDown(false);
        onClose();
      }}
      isOpen={dropDownStatus}
      {...rest}
      targetRenderer={() => (
        <div>
          <div
            className={`smallplus 
        ${styles.targetWrapper}
        ${!readOnly && !dropDownStatus && !isEmpty(value) ? styles.active : ""}
        ${!readOnly && !disabled && dropDownStatus ? styles.activeLabel : ""}
        ${disabled ? styles.disabled : ""}
        ${readOnly ? styles.readOnly : ""}
        ${errors.length > 0 && styles.error}
        ${className}
        `}
          >
            {isEmpty(value) && placeholder && (
              <p className={styles.placeholderTxt}>{placeholder}</p>
            )}
            {!isEmpty(value) && (
              <div
                className={`smallplus600 ${styles.labelDefaultStyle} sleekdropdown_label`}
              >
                <OverflowText line={1}>{labelWithProps}</OverflowText>
              </div>
            )}
            {onClear && !readOnly && !isEmpty(value) && (
              <ClearView
                onClear={() => {
                  if (!disabled) {
                    setDropDownStatus(false);
                    onClear();
                  }
                }}
              />
            )}
            {!readOnly && <DropDownIcon className={styles.dropdownSvg} />}
          </div>
          {hint && <FieldHint hint={hint} targetId={id} />}
          {errors && errors.length > 0 && (
            <FieldErrors errors={errors} targetId={id} />
          )}
        </div>
      )}
      listRenderer={({ closePopup: closeDropDown }) => {
        function closePopup() {
          closeDropDown();
          handleDropDown(false);
        }
        if (!readOnly && !disabled && listRenderer) {
          return (
            <div className={styles.listRendererWrapper}>
              {placeholder && (
                <p className={`smallplus ${styles.dropdownTitle}`}>
                  {placeholder}
                </p>
              )}
              {listRenderer && listRenderer({ closePopup })}
            </div>
          );
        }
      }}
    />
  );
}

SleekDropDown.propTypes = {
  /**
   * Label is used to display title of the fields
   */
  label: PropTypes.string,
  /**
   * Selected value will be passed by value prop
   */
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string
  ]),
  /**
   * User can customise dropdown list with this prop
   */
  listRenderer: PropTypes.func,
  /**
   * Trigger when user click close icon
   */
  onClear: PropTypes.func,
  /**
   * Placeholder for the sleek dropdown
   */
  placeholder: PropTypes.string,
  /**
   * Disabled state determined by this prop
   */
  disabled: PropTypes.bool,
  /**
   * Readonly state determined by this prop
   */
  readOnly: PropTypes.bool,
  /**
   * Error in the dropdown are listed in this prop
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Sleek Dropdown hint will be passed via this prop
   */
  hint: PropTypes.string,
  id: PropTypes.string,
  /**
   * Trigger when user closes the dropdown
   */
  onClose: PropTypes.func,
  /**
   * Trigger when user opens the dropdown
   */
  onOpen: PropTypes.func,
  /**
   * Dropdown open based on this props
   */
  isOpen: PropTypes.bool,
  /**
   * Override the default styles of the sleek component
   */
  className: PropTypes.string
};

SleekDropDown.defaultProps = {
  label: null,
  value: null,
  placeholder: null,
  listRenderer: null,
  onClear: null,
  disabled: false,
  readOnly: false,
  errors: [],
  hint: null,
  id: "sleekdropdown",
  onClose: () => {},
  onOpen: () => {},
  isOpen: false
};

export const UserLabel = ({ value, info }) => {
  let { _id, Name } = info;
  return (
    <div className={`smallplus600 ${styles.userLabelWrapper}`}>
      <UserIcon
        className={styles.itemProfileImage}
        userId={_id}
        name={Name}
        size={AVATAR_SIZE.SIZE16}
      />
      <p className={styles.textLabel}>{Name}</p>
      {Array.isArray(value) && value.length > 1 && (
        <p className={styles.moreTxt}>
          <Trans>and {value.length - 1} more</Trans>
        </p>
      )}
    </div>
  );
};

UserLabel.propTypes = {
  info: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    Name: PropTypes.string
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
};

export const MultiSelectLabel = ({ label, value, className }) => {
  let [count, setCount] = useState(0);

  useEffect(
    function valueEffect() {
      getSelectedValueCount(value);
    },
    [value]
  );

  function getSelectedValueCount(value) {
    let count = 0;
    let type = checkValueType(value);
    if (type === "Object") {
      Object.keys(value)?.forEach((key) => {
        value[key]?.forEach(() => {
          count = count + 1;
        });
      });
    } else if (type === "Array") {
      count = value?.length;
    }
    setCount(count);
  }

  function getFirstValue(value) {
    if (typeof label === "function") {
      let item = "";
      if (Array.isArray(value)) {
        item = value[0];
      } else if (typeof value === "object" && value !== null) {
        let valueList = [];
        Object.keys(value).forEach(
          (key) => (valueList = [...valueList, ...value[key]])
        );
        item = valueList[0];
      }
      if (item) {
        return label(item);
      }
    }
    return label;
  }

  return (
    <div className={`smallplus600 ${styles.userLabelWrapper} ${className}`}>
      <p className={styles.textLabel}>{getFirstValue(value)}</p>
      {count > 1 && (
        <p className={styles.moreTxt}>
          <Trans>and {count - 1} more</Trans>
        </p>
      )}
    </div>
  );
};

MultiSelectLabel.defaultProps = {
  className: ""
};

MultiSelectLabel.propTypes = {
  label: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string
};

const ClearView = ({ onClear }) => {
  return (
    <div
      onClick={(e) => {
        onClear();
        e.stopPropagation();
      }}
      className={styles.ClearIcon}
    >
      <ClearIcon />
    </div>
  );
};

ClearView.propTypes = {
  onClear: PropTypes.func
};

export function checkValueType(value) {
  if (Array.isArray(value)) {
    return "Array";
  } else if (typeof value === "object" && value !== null) return "Object";
  return "";
}

export const DropDownWrapper = (props) => {
  return <div className={styles.dropdownItemWrapper}>{props.children}</div>;
};

DropDownWrapper.propTypes = {
  children: PropTypes.element
};

export const NoResultsFound = ({ searchText }) => {
  return searchText ? (
    <p className={`${styles.noResultsFound} smallplus600`}>
      <Trans>No data found</Trans>
    </p>
  ) : (
    <p className={`${styles.noResultsFound} smallplus600`}>
      <Trans>No results found</Trans>
    </p>
  );
};
NoResultsFound.propTypes = {
  searchText: PropTypes.string
};
